import React, { useEffect, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';
import { holidayApi } from '../api/axios';
import HolidayRecommendationStatus from './HolidayRecommendationStatus';
import HolidayApprovalPanel from './HolidayApprovalPanel';

const HolidayCalendarPanel = ({ t }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [calendar, setCalendar] = useState({ holidays: [], system_calendar_review_required: false });
  const [form, setForm] = useState({ name: '', holiday_date: '' });
  const [saving, setSaving] = useState(false);

  const loadCalendar = async () => {
    try {
      const response = await holidayApi.getCalendar(year);
      setCalendar(response.data || { holidays: [], system_calendar_review_required: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('err_holiday_calendar', 'Resmi tatil takvimi yüklenemedi.'));
    }
  };

  useEffect(() => {
    loadCalendar();
  }, [year]);

  const addHoliday = async (event) => {
    event.preventDefault();
    if (!form.name || !form.holiday_date) return;
    try {
      setSaving(true);
      await holidayApi.add(form.name, form.holiday_date);
      setForm({ name: '', holiday_date: '' });
      await loadCalendar();
      toast.success(t('msg_holiday_added', 'Şirket özel tatili eklendi.'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('err_holiday_add', 'Tatil eklenemedi.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-8 rounded-[2rem] border border-amber-100 bg-amber-50 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-amber-700">{t('settings_holiday_kicker', 'Resmi Tatil Takvimi')}</p>
          <h3 className="mt-2 text-lg font-black uppercase tracking-tight text-slate-900">{t('settings_holiday_title', 'Sistem + şirket özel günleri')}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">{t('settings_holiday_desc', 'Doğrulanmış sistem takvimi ve şirket özel günleri puantajda birlikte değerlendirilir.')}</p>
        </div>
        <div className="flex items-center gap-3">
          <CalendarDays className="text-amber-700" size={22} />
          <select value={year} onChange={(event) => setYear(Number(event.target.value))} className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm font-bold">
            {Array.from({ length: 17 }, (_, index) => new Date().getFullYear() - 1 + index).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </div>
      </div>

      {calendar.system_calendar_review_required && (
        <p className="mt-4 rounded-xl border border-amber-200 bg-white px-4 py-3 text-xs font-semibold text-amber-800">
          {t('settings_holiday_review_required', 'Bu ülke/yıl için sistem takvimi henüz HR/muhasebe onayından geçmedi. Şirket özel günleri manuel eklenebilir.')}
        </p>
      )}

      <div className="mt-4 space-y-2">
        {calendar.holidays.length ? calendar.holidays.map((holiday) => (
          <div key={`${holiday.holiday_date}-${holiday.name}`} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm">
            <span className="font-semibold text-slate-800">{holiday.name}</span>
            <span className="text-xs font-bold text-slate-500">{holiday.holiday_date} · {holiday.source === 'SYSTEM' ? t('holiday_source_system', 'Sistem') : t('holiday_source_company', 'Şirket')}</span>
          </div>
        )) : <p className="text-sm text-slate-500">{t('settings_holiday_empty', 'Bu yıl için kayıtlı tatil bulunmuyor.')}</p>}
      </div>

      <form onSubmit={addHoliday} className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px_auto]">
        <input value={form.name} onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))} placeholder={t('settings_holiday_name', 'Örn. Şirket kuruluş günü')} className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm" />
        <input type="date" value={form.holiday_date} onChange={(event) => setForm((previous) => ({ ...previous, holiday_date: event.target.value }))} className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-sm" />
        <button type="submit" disabled={saving} className="rounded-xl bg-amber-600 px-4 py-3 text-[10px] font-black uppercase tracking-[0.15em] text-white disabled:opacity-50">{t('settings_holiday_add', 'Özel gün ekle')}</button>
      </form>
      <HolidayRecommendationStatus t={t} />
      <HolidayApprovalPanel t={t} />
    </section>
  );
};

export default HolidayCalendarPanel;
